import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isEmail } from "validator";
import styles from "./LoginForm.module.css";
import { authenticateUser, updatePassword } from "../../dbApiFunctions";
import { getAuth, signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { hashString } from "../../utils/utilities";
import { showSuccessAlert, showErrorAlert } from "../../utils/sweetAlertConfig";
import "../../styles/sweetAlert.css";
import ForgotPassword from "../userAccount/forgotPassword/ForgotPassword";
import "../loginRegister/loginStyle.css";

const LOGIN_IMAGE = "https://cdn.builder.io/api/v1/image/assets/TEMP/9b0aaa48654bf4a1d5b0e6a63be36a7be59cc971?width=2086";

export default function Login({ setIsLoggedIn, setFormType }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [successful, setSuccessful] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [accountLocked, setAccountLocked] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const MAX_ATTEMPTS = 5;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccessful(false);

    if (accountLocked) {
      showErrorAlert(
        'Account Locked',
        'Account is temporarily locked due to too many failed attempts.',
        'Please try again later or contact support'
      );
      return;
    }

    setIsLoggingIn(true);

    if (!validateForm()) {
      setIsLoggingIn(false);
      return;
    }

    try {
      // Authenticate with Firebase
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        setCurrentUser(userCredential.user);
        setShowEmailVerification(true);
        setIsLoggingIn(false);
        
        showErrorAlert(
          'Email Not Verified',
          'Please verify your email address before logging in.',
          'Check your inbox for the verification link or request a new one.'
        );
        return;
      }

      // If Firebase authentication is successful and email is verified, proceed with user data setup
      const res = await updatePassword(formData.email, formData.password);
      
      // Reset login attempts on successful login
      setLoginAttempts(0);
      const hashedEmailValue = await hashString(formData.email);
      sessionStorage.setItem("username", hashedEmailValue);
      sessionStorage.setItem("email", formData.email);
      sessionStorage.setItem("password", formData.password);
      setIsLoggedIn(true);
      
      showSuccessAlert(
        'Login Successful!',
        'Welcome back!',
        'Redirecting to your dashboard...'
      );

      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (firebaseError) {
      console.error("Firebase authentication error:", firebaseError);
      
      // Increment login attempts on failed authentication
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        setAccountLocked(true);
        showErrorAlert(
          'Account Locked',
          'Maximum login attempts reached (5).',
          'Account is temporarily locked. Please try again later.'
        );
      } else {
        // Customize error message based on Firebase error code
        let errorMessage = "Invalid email or password";
        let errorTitle = "Login Failed";
        let errorDetails = "Please check your credentials and try again.";

        switch (firebaseError.code) {
          case 'auth/invalid-credential':
            errorMessage = "The email or password you entered is incorrect";
            errorTitle = "Invalid Credentials";
            errorDetails = "Please check your email and password.";
            break;
          case 'auth/user-not-found':
            errorMessage = "No account found with this email address";
            errorTitle = "Account Not Found";
            errorDetails = "Please check your email or create a new account.";
            break;
          case 'auth/wrong-password':
            errorMessage = "Incorrect password. Please try again";
            errorTitle = "Wrong Password";
            errorDetails = "Please check your password and try again.";
            break;
          case 'auth/too-many-requests':
            errorMessage = "Too many failed attempts. Please try again later";
            errorTitle = "Too Many Attempts";
            errorDetails = "Please wait a moment before trying again.";
            break;
          case 'auth/user-disabled':
            errorMessage = "This account has been disabled";
            errorTitle = "Account Disabled";
            errorDetails = "Please contact support for assistance.";
            break;
          case 'auth/network-request-failed':
            errorMessage = "Network error. Please check your connection";
            errorTitle = "Network Error";
            errorDetails = "Please ensure you have a stable internet connection.";
            break;
          default:
            errorMessage = "Login failed. Please try again";
            errorTitle = "Login Error";
            errorDetails = "An unexpected error occurred. Please try again.";
        }

        showErrorAlert(
          errorTitle,
          errorMessage,
          errorDetails
        );
      }

      // Clear password field on failed attempt
      setFormData(prev => ({ ...prev, password: "" }));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    if (!currentUser) return;
    
    setIsSendingVerification(true);
    try {
      await sendEmailVerification(currentUser);
      showSuccessAlert(
        'Verification Email Sent!',
        'Please check your inbox for the verification link.',
        'Make sure to check your spam folder as well.'
      );
    } catch (error) {
      console.error("Error sending verification email:", error);
      showErrorAlert(
        'Failed to Send',
        'Error sending verification email.',
        'Please try again or contact support.'
      );
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleEmailVerificationCheck = async () => {
    try {
      if (!currentUser) return;
      
      await currentUser.reload();
      
      if (currentUser.emailVerified) {
        setShowEmailVerification(false);
        setCurrentUser(null);
        
        // Proceed with login
        const res = await updatePassword(formData.email, formData.password);
        
        // Reset login attempts on successful login
        setLoginAttempts(0);
        const hashedEmailValue = await hashString(formData.email);
        sessionStorage.setItem("username", hashedEmailValue);
        sessionStorage.setItem("email", formData.email);
        sessionStorage.setItem("password", formData.password);
        setIsLoggedIn(true);
        
        showSuccessAlert(
          'Login Successful!',
          'Welcome back!',
          'Redirecting to your dashboard...'
        );

        // Reload after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        showErrorAlert(
          'Email Not Verified',
          'Please check your email and click the verification link.',
          'If you haven\'t received the email, try resending it.'
        );
      }
    } catch (error) {
      console.error("Error checking email verification:", error);
      showErrorAlert(
        'Verification Check Failed',
        'Error checking email verification status.',
        'Please try again or contact support.'
      );
    }
  };

  const handleBackToLogin = () => {
    setShowEmailVerification(false);
    setCurrentUser(null);
    setFormData({ email: "", password: "" });
  };

  const handleSignUpClick = (e) => {
    e.preventDefault();
    setFormType("register");
  };

  return (
    <>
      {showForgotPassword ? (
        <ForgotPassword onBack={() => setShowForgotPassword(false)} />
      ) : showEmailVerification ? (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '40px',
            textAlign: 'center'
          }}>
            <div style={{
              marginBottom: '24px'
            }}>
              <span style={{
                fontSize: '28px',
                fontWeight: '700',
                background: 'linear-gradient(270deg, #7040ff 0%, #4b1bf8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                Shutter
              </span>
            </div>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '24px',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              Email Verification Required
            </h2>

            <div style={{
              background: 'rgba(14, 165, 233, 0.1)',
              border: '1px solid rgba(14, 165, 233, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'inline-block'
            }}>
              <p style={{
                color: '#0c4a6e',
                fontSize: '16px',
                fontWeight: '600',
                margin: '0',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {formData.email}
              </p>
            </div>

            <p style={{
              color: '#0369a1',
              fontSize: '15px',
              lineHeight: '1.6',
              marginBottom: '24px',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              Please check your inbox and click the verification link to complete your login.
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <button
                onClick={handleEmailVerificationCheck}
                style={{
                  cursor: 'pointer',
                  background: 'linear-gradient(270deg, #7040ff 0%, #4b1bf8 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 24px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(112, 64, 255, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(112, 64, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(112, 64, 255, 0.3)';
                }}
              >
                I've Verified My Email
              </button>

              <button
                onClick={handleResendVerificationEmail}
                disabled={isSendingVerification}
                style={{
                  cursor: isSendingVerification ? 'not-allowed' : 'pointer',
                  background: 'transparent',
                  border: '2px solid #7040ff',
                  borderRadius: '12px',
                  padding: '16px 24px',
                  color: '#7040ff',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease',
                  opacity: isSendingVerification ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSendingVerification) {
                    e.target.style.background = '#7040ff';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSendingVerification) {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#7040ff';
                  }
                }}
              >
                {isSendingVerification ? 'Sending...' : 'Resend Verification Email'}
              </button>

              <button
                onClick={handleBackToLogin}
                style={{
                  cursor: 'pointer',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 24px',
                  color: '#6b7280',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#6b7280';
                }}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="auth-split-layout-half">
          <div className="auth-left-half">
            <div className="auth-branding">
              <span className="auth-logo">Shutter</span>
              <a href="/" className="auth-back-link">Back to Home</a>
            </div>
            <div className="auth-form-card">
              <section className={styles.formContainer}>
                <h2 className="auth-title">Login into Your Account</h2>
                <form onSubmit={handleLogin}>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Email</label>
                    <div className={styles.inputField}>
                      <input
                        type="email"
                        name="email"
                        className={styles.input}
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleInputChange}
                        autoComplete="email"
                        disabled={isLoggingIn}
                      />
                    </div>
                    {errors.email && (
                      <div style={{ 
                        color: '#ef4444', 
                        fontSize: '12px', 
                        marginTop: '4px'
                      }}>
                        {errors.email}
                      </div>
                    )}
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Password</label>
                    <div className={styles.inputField}>
                      <input
                        type="password"
                        name="password"
                        className={styles.input}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleInputChange}
                        autoComplete="current-password"
                        disabled={isLoggingIn}
                      />
                    </div>
                    {errors.password && (
                      <div style={{ 
                        color: '#ef4444', 
                        fontSize: '12px', 
                        marginTop: '4px'
                      }}>
                        {errors.password}
                      </div>
                    )}
                  </div>
                  
                  <button 
                    type="submit" 
                    className={styles.submitButton}
                    disabled={isLoggingIn || accountLocked}
                    style={{ opacity: (isLoggingIn || accountLocked) ? 0.7 : 1 }}
                  >
                    <span className={styles.buttonText}>
                      {isLoggingIn ? "Logging in..." : accountLocked ? "Account Locked" : "Login"}
                    </span>
                  </button>
                </form>
                <div className={styles.linksSection}>
                  <p className={styles.signupPrompt}>
                    Don't have an account?
                    <a href="#" className={styles.signupLink} onClick={handleSignUpClick}>
                      Sign up
                    </a>
                  </p>
                  <a
                    href="#"
                    className={styles.forgotPasswordLink}
                    onClick={e => { e.preventDefault(); setShowForgotPassword(true); }}
                  >
                    Forgot Password?
                  </a>
                </div>
              </section>
            </div>
          </div>
          <div className="auth-right-half" style={{ backgroundImage: `url('${LOGIN_IMAGE}')` }} />
        </div>
      )}
    </>
  );
}
